<script setup>
/**
 * The Bindle logo: the original hand-drawn wordmark, traced to vector.
 *
 * Both assets are painted through a CSS mask rather than embedded as images, so
 * one file serves every treatment. The wordmark takes `--wordmark`, the chosen
 * ink→teal diagonal, which is re-cut per theme in app.css — on the dark page the
 * ink end is within a hair of the background, so the gradient inverts rather
 * than being recoloured.
 *
 * Assets: public/brand/bindle-wordmark.svg, public/brand/bindle-monogram.svg
 * Reference: documentation/branding.html § Logo
 */
defineProps({
  // Mark only, no wordmark — for the drawer header, avatars and tight bars.
  compact: { type: Boolean, default: false },
  // Reversed lock-up for brand-coloured bands and photographs.
  inverse: { type: Boolean, default: false },
})
</script>

<template>
  <span class="inline-flex items-center gap-2.5">
    <span
      class="grid aspect-square w-9 place-items-center rounded-[26%]"
      :class="inverse ? 'bg-white' : 'mark-tile'"
      aria-hidden="true"
    >
      <span
        class="monogram block aspect-[252/343] w-[46%]"
        :class="inverse ? 'bg-brand' : 'bg-brand-contrast'"
      ></span>
    </span>
    <span
      v-if="!compact"
      class="wordmark block h-[21px] w-[74px]"
      :class="inverse ? 'wordmark-inverse' : 'wordmark-primary'"
      role="img"
      aria-label="Bindle"
    ></span>
  </span>
</template>

<style scoped>
.monogram {
  -webkit-mask: url('/brand/bindle-monogram.svg') center / contain no-repeat;
  mask: url('/brand/bindle-monogram.svg') center / contain no-repeat;
}

.wordmark {
  -webkit-mask: url('/brand/bindle-wordmark.svg') left center / contain no-repeat;
  mask: url('/brand/bindle-wordmark.svg') left center / contain no-repeat;
}

/* The tile carries the same diagonal as the wordmark, so the lock-up reads as
   one object rather than a square next to some letters. */
.mark-tile {
  background: var(--wordmark-tile);
}

.wordmark-primary {
  background: var(--wordmark);
}

.wordmark-inverse {
  background: var(--wordmark-on-brand);
}
</style>
